import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Layout from '../components/Layout';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const { Text, Title } = Typography;

interface AccessResponse {
  can_access: boolean;
  is_admin: boolean;
  email: string;
}

interface DefaultsResponse {
  source_images_path: string;
  model_csv_path: string;
  preview_limit_max: number;
  user_email: string;
}

interface PreviewSample {
  model: string;
  brand: string;
  filename: string;
  source_path: string;
  relative_path: string;
}

interface PreviewTopModel {
  model: string;
  brand?: string;
  images: number;
}

interface PreviewResponse {
  source_images_path: string;
  model_csv_path: string;
  models_in_model_csv: number;
  matched_models: number;
  total_images_scanned: number;
  recognized_images: number;
  unresolved_images: number;
  top_models: PreviewTopModel[];
  samples: PreviewSample[];
  unresolved_samples: string[];
}

const toModelList = (raw: string): string[] =>
  raw
    .split(/[,\n;]/g)
    .map((value) => value.trim())
    .filter(Boolean);

const AutoBrandBuilder: React.FC = () => {
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [canAccess, setCanAccess] = useState(false);
  const [defaults, setDefaults] = useState<DefaultsResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [buildLoading, setBuildLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [selectedSourcePaths, setSelectedSourcePaths] = useState<React.Key[]>([]);
  const [createdTestId, setCreatedTestId] = useState<number | null>(null);
  const [createdMessage, setCreatedMessage] = useState<string>('');
  const [form] = Form.useForm();

  const loadAccess = useCallback(async () => {
    setLoadingAccess(true);
    try {
      const { data } = await api.get<AccessResponse>('/admin/auto-brand/access');
      setCanAccess(Boolean(data.can_access));
    } catch (error: any) {
      setCanAccess(false);
      message.error(getApiErrorMessage(error, 'Не удалось проверить доступ'));
    } finally {
      setLoadingAccess(false);
    }
  }, []);

  const loadDefaults = useCallback(async () => {
    try {
      const { data } = await api.get<DefaultsResponse>('/admin/auto-brand/defaults');
      setDefaults(data);
      form.setFieldsValue({
        source_images_path: data.source_images_path,
        model_csv_path: data.model_csv_path,
        title: 'Тест: определение модели авто по фото',
        description: 'Автоматически собранный тест по фотографиям автомобилей',
        question_text: 'Определите модель автомобиля по фото',
        question_count: 20,
        answer_options: 4,
        preview_limit: Math.min(200, data.preview_limit_max || 200),
        time_limit_minutes: 20,
      });
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Не удалось загрузить настройки по умолчанию'));
    }
  }, [form]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    if (canAccess) {
      loadDefaults();
    }
  }, [canAccess, loadDefaults]);

  const previewColumns = useMemo<ColumnsType<PreviewSample>>(
    () => [
      {
        title: 'Модель',
        dataIndex: 'model',
        key: 'model',
        render: (value: string) => <Tag color="blue">{value}</Tag>,
      },
      {
        title: 'Марка',
        dataIndex: 'brand',
        key: 'brand',
        width: 180,
      },
      {
        title: 'Файл',
        dataIndex: 'filename',
        key: 'filename',
        ellipsis: true,
      },
      {
        title: 'Путь',
        dataIndex: 'relative_path',
        key: 'relative_path',
        ellipsis: true,
      },
    ],
    []
  );

  const topModelsColumns = useMemo<ColumnsType<PreviewTopModel>>(
    () => [
      { title: 'Модель', dataIndex: 'model', key: 'model' },
      { title: 'Марка', dataIndex: 'brand', key: 'brand' },
      { title: 'Фото', dataIndex: 'images', key: 'images', width: 120 },
    ],
    []
  );

  const handlePreview = async () => {
    try {
      const values = await form.validateFields(['source_images_path', 'model_csv_path', 'brands_raw', 'preview_limit']);
      setPreviewLoading(true);
      setCreatedTestId(null);
      setCreatedMessage('');
      const selectedModels = toModelList(values.brands_raw || '');
      const { data } = await api.post<PreviewResponse>('/admin/auto-brand/preview', {
        source_images_path: values.source_images_path,
        model_csv_path: values.model_csv_path,
        selected_models: selectedModels.length ? selectedModels : undefined,
        preview_limit: Number(values.preview_limit || 200),
      });
      setPreview(data);
      setSelectedSourcePaths([]);
      message.success('Предпросмотр собран');
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Ошибка предпросмотра'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBuild = async () => {
    try {
      const values = await form.validateFields([
        'source_images_path',
        'model_csv_path',
        'title',
        'description',
        'question_text',
        'question_count',
        'answer_options',
        'time_limit_minutes',
        'append_test_id',
        'brands_raw',
        'random_seed',
      ]);
      setBuildLoading(true);
      const selectedModels = toModelList(values.brands_raw || '');
      const payload: any = {
        source_images_path: values.source_images_path,
        model_csv_path: values.model_csv_path,
        title: values.title,
        description: values.description,
        question_text: values.question_text,
        question_count: Number(values.question_count || 20),
        answer_options: Number(values.answer_options || 4),
        selected_models: selectedModels.length ? selectedModels : undefined,
        random_seed: values.random_seed !== undefined && values.random_seed !== null ? Number(values.random_seed) : undefined,
        time_limit_minutes: Number(values.time_limit_minutes || 20),
        target_test_id: values.append_test_id ? Number(values.append_test_id) : undefined,
      };
      if (selectedSourcePaths.length > 0 && preview) {
        payload.selected_images = preview.samples
          .filter((sample) => selectedSourcePaths.includes(sample.source_path))
          .map((sample) => sample.source_path);
      }
      const { data } = await api.post('/admin/auto-brand/build', payload);
      setCreatedMessage(data.message || 'Тест собран');
      setCreatedTestId(data.test_id || null);
      message.success(data.message || 'Тест собран');
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Ошибка сборки теста'));
    } finally {
      setBuildLoading(false);
    }
  };

  if (loadingAccess) {
    return (
      <Layout>
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  if (!canAccess) {
    return (
      <Layout>
        <div style={{ padding: 24 }}>
          <Alert
            type="warning"
            showIcon
            message="Нет доступа к конструктору авто-тестов"
            description="Доступ есть у администраторов и пользователей из списка TEST_EDITOR_EMAILS."
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: 24 }}>
        <Title level={3} style={{ marginTop: 0 }}>
          Конструктор авто-теста (модель по фото)
        </Title>
        <Text type="secondary">
          Собирает вопросы из фото и model.csv. Варианты ответа формируются по моделям.
        </Text>

        <Card style={{ marginTop: 16 }}>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Папка с фото" name="source_images_path" rules={[{ required: true }]}>
                  <Input placeholder="/app/data/source_images/brands" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Файл model.csv" name="model_csv_path" rules={[{ required: true }]}>
                  <Input placeholder="/app/data/source_data/model.csv" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Название теста" name="title" rules={[{ required: true, min: 3 }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Описание" name="description">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Текст вопроса" name="question_text" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label="Кол-во вопросов" name="question_count" rules={[{ required: true }]}>
                  <InputNumber min={1} max={500} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label="Вариантов ответа" name="answer_options" rules={[{ required: true }]}>
                  <InputNumber min={2} max={8} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label="Лимит, мин" name="time_limit_minutes" rules={[{ required: true }]}>
                  <InputNumber min={1} max={300} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Модели (через запятую, опционально)" name="brands_raw">
                  <Input.TextArea rows={2} placeholder="TOYOTA RAV4 ACA2#..., ACURA CL YA1..." />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label="Preview limit" name="preview_limit">
                  <InputNumber min={1} max={defaults?.preview_limit_max || 1000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label="Random seed (опц.)" name="random_seed">
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Добавить в существующий тест (ID, опц.)"
                  name="append_test_id"
                  tooltip="Если пусто, создастся новый тест"
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Space>
              <Button onClick={handlePreview} loading={previewLoading}>
                Проверить источники
              </Button>
              <Button type="primary" onClick={handleBuild} loading={buildLoading}>
                Собрать тест
              </Button>
            </Space>
          </Form>
        </Card>

        {preview && (
          <>
            <Divider />
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Card>
                  <Statistic title="Всего фото" value={preview.total_images_scanned} />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card>
                  <Statistic title="Распознано" value={preview.recognized_images} />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card>
                  <Statistic title="Без распознавания" value={preview.unresolved_images} />
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card>
                  <Statistic title="Моделей в model.csv" value={preview.models_in_model_csv} />
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} lg={8}>
                <Card title="Топ моделей по фото">
                  <Table
                    rowKey={(record) => record.model}
                    columns={topModelsColumns}
                    dataSource={preview.top_models}
                    size="small"
                    pagination={{ pageSize: 8 }}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={16}>
                <Card title="Сэмпл фото (можно выбрать вручную)">
                  <Table
                    rowKey={(record) => record.source_path}
                    columns={previewColumns}
                    dataSource={preview.samples}
                    size="small"
                    pagination={{ pageSize: 10 }}
                    rowSelection={{
                      selectedRowKeys: selectedSourcePaths,
                      onChange: (keys) => setSelectedSourcePaths(keys),
                    }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}

        {(createdMessage || createdTestId) && (
          <Card style={{ marginTop: 16 }}>
            <Alert
              showIcon
              type="success"
              message={createdMessage || 'Тест создан'}
              description={
                <Space direction="vertical">
                  {createdTestId ? <Text>Test ID: {createdTestId}</Text> : null}
                  {createdTestId ? (
                    <a href={`/tests/${createdTestId}`} target="_blank" rel="noreferrer">
                      Открыть тест в новой вкладке
                    </a>
                  ) : null}
                </Space>
              }
            />
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AutoBrandBuilder;
